/**
 * The skill tree screen. Everything inside #skillsRoot, talking to /api/skills.
 *
 * Two levels. The tree lists every skill in the sport with what the player has
 * actually put against it, and one skill opens into its own page: what good
 * looks like, the drills, the specialists whose technique the companion
 * studied, the cited sources it read, and the player's own log for that skill.
 *
 * Two kinds of number and they are never mixed. "Tracked" is what the shot
 * tracker measured. "Logged" is what the player typed in. Every tile says
 * which one it is, and a skill with nothing against it says so plainly instead
 * of showing a zero dressed up as progress.
 *
 * The line under the specialists is not decoration. The app studies publicly
 * documented technique and cites what it read. It is not trained on these
 * players and they are not involved with it. The Worker sends that wording
 * down with every response; this screen shows it wherever a name appears.
 */

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** A source link is only ever a web page. Anything else is shown as plain text. */
const safeUrl = (u) => { try { const p = new URL(String(u)); return p.protocol === 'http:' || p.protocol === 'https:' ? p.href : ''; } catch { return ''; } };

const RATINGS = [1, 2, 3, 4, 5];
const FRESH_DAYS = 30;          // "working on it" versus "gone cold"
const OTHER = '__other__';

const n0 = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmt = (v) => Math.round(n0(v)).toLocaleString();
const daysSince = (iso) => { const t = Date.parse(iso); return Number.isFinite(t) ? Math.floor((Date.now() - t) / 864e5) : null; };

function whenLabel(iso) {
  const d = daysSince(iso);
  if (d === null) return '';
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 60) return `${d} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
const stamp = (iso) => { const t = Date.parse(iso); return Number.isFinite(t) ? new Date(t).toLocaleString() : ''; };

/* ------------------------------------------------------------------ state
 * Module level so an open skill and a half filled form survive a repaint and
 * a trip to another view. */
const blankForm = () => ({ pick: '', other: '', minutes: '', reps: '', rating: null, note: '' });

let ctx = null, root = null, panel = null;
const S = {
  data: null, err: null,
  open: null,                                   // the skill id being read, or null for the tree
  detail: null, detailErr: null, loading: false,
  form: { pick: '', other: '', minutes: '', reps: '', rating: null, note: '' },
};

/* ------------------------------------------------------------------- boot */

export function mount(c) {
  ctx = c;
  root = document.getElementById('skillsRoot');
  if (!root) return;
  root.innerHTML = `
    <div class="grid-stats skstrip" id="skStrip"></div>
    <div id="skPanel"></div>`;
  panel = root.querySelector('#skPanel');
}

export async function render() {
  if (!root) return;
  if (!S.data) paint();                         // never leave it blank while the first call is out
  await refresh();
}

async function refresh() {
  try { S.data = await ctx.api('/api/skills'); S.err = null; }
  catch (e) { S.err = e.message; }
  if (S.open) await loadSkill(S.open, true);
  else paint();
}

async function loadSkill(id, quiet) {
  S.loading = true;
  if (!quiet) paint();
  try { S.detail = await ctx.api(`/api/skills/${encodeURIComponent(id)}`); S.detailErr = null; }
  catch (e) { S.detail = null; S.detailErr = e.message; }
  S.loading = false;
  paint();
}

/* ------------------------------------------------------------------ paint */

const bind = (sel, ev, fn, scope = panel) => scope?.querySelectorAll(sel).forEach((el) => el.addEventListener(ev, fn));

function paint() {
  if (!panel) return;
  paintStrip();
  if (S.err) {
    panel.innerHTML = `<div class="card"><h3>Skills</h3><p class="sub">${esc(S.err)}</p>
      <button class="btn" id="skRetry" style="margin-top:12px">Try again</button></div>`;
    bind('#skRetry', 'click', refresh);
    return;
  }
  if (!S.data) { panel.innerHTML = '<div class="card"><h3>Skills</h3><p class="sub">Loading the tree.</p></div>'; return; }
  try { if (S.open) paintDetail(); else paintTree(); }
  catch (e) { panel.innerHTML = `<div class="card"><h3>Skills</h3><p class="sub">${esc(e?.message || 'That did not load.')}</p></div>`; }
}

function paintStrip() {
  const el = root?.querySelector('#skStrip');
  if (!el) return;
  const d = S.data;
  if (!d) { el.innerHTML = ''; return; }
  const t = d.totals || {};
  const tiles = [
    `<div class="stat"><b>${fmt(t.worked)}/${fmt(t.skills)}</b><small>skills worked</small></div>`,
    `<div class="stat"><b>${fmt(t.sessions)}</b><small>drills logged</small></div>`,
    t.minutes ? `<div class="stat"><b>${fmt(t.minutes)}</b><small>minutes logged</small></div>` : '',
    t.reps ? `<div class="stat"><b>${fmt(t.reps)}</b><small>reps logged</small></div>` : '',
    d.tracked ? `<div class="stat"><b>${fmt(d.tracked.attempts)}</b><small>shots tracked</small></div>` : '',
  ].filter(Boolean);
  el.innerHTML = tiles.join('');
}

/* ================================================================= the tree */

function stateOf(logged) {
  if (!logged || !logged.sessions) return { cls: '', label: 'Not started' };
  const d = daysSince(logged.lastAt);
  if (d !== null && d <= FRESH_DAYS) return { cls: 'good', label: `Worked ${whenLabel(logged.lastAt)}` };
  return { cls: 'cool', label: whenLabel(logged.lastAt) || 'Logged' };
}

function paintTree() {
  const d = S.data;
  const sug = d.suggestion;
  const measured = d.skills.find((s) => s.measured);

  panel.innerHTML = `
    ${sug ? `
      <div class="card sknext">
        <h3>Work on this next</h3>
        <div class="sknext-b">
          <div>
            <b>${esc(sug.label)}</b>
            <p class="sub">${esc(sug.why)}</p>
            ${sug.drill ? `<p class="skdrill"><b>${esc(sug.drill.name)}</b> ${esc(sug.drill.how)}</p>` : ''}
            ${sug.note ? `<p class="skplain">${esc(sug.note)}</p>` : ''}
          </div>
          <button class="btn primary" data-open="${esc(sug.skill)}">Open ${esc(sug.label)}</button>
        </div>
      </div>` : ''}

    ${d.tracked && measured ? `
      <div class="card">
        <h3>Measured in the app</h3>
        <p class="sub">${esc(measured.label)} is the one the shot tracker watches, so these are counted, not typed in.</p>
        <div class="grid-stats" style="margin-top:12px">
          <div class="stat"><b>${fmt(d.tracked.attempts)}</b><small>attempts tracked</small></div>
          <div class="stat"><b>${fmt(d.tracked.makes)}</b><small>makes, of ${fmt(d.tracked.judged)} judged</small></div>
          <div class="stat"><b>${fmt(d.tracked.sessions)}</b><small>sessions</small></div>
        </div>
      </div>` : ''}

    <div class="card">
      <h3>${esc(d.sport)} skills</h3>
      <p class="sub">The whole sport, not just the shot. Open one to see what good looks like, the drills, and who your companion studied for it.</p>
      <div class="skgrid">
        ${d.skills.map(skillCard).join('')}
      </div>
    </div>

    ${d.recent?.length ? `
      <div class="card">
        <h3>What you logged</h3>
        <div class="skrows">${d.recent.map((e) => logRow(e, d.skills.find((s) => s.id === e.skill)?.label || e.skill, e.drill)).join('')}</div>
      </div>` : ''}

    <p class="skplain">${esc(d.note)}</p>`;

  bind('[data-open]', 'click', (e) => openSkill(e.currentTarget.dataset.open));
  // The recent rows carry a delete button too, so they need the same handler
  // the skill page binds. Without this the button on the tree does nothing.
  bind('[data-del]', 'click', (e) => removeEntry(e.currentTarget.dataset.del));
}

function skillCard(s) {
  const st = stateOf(s.logged);
  const bits = s.logged.sessions
    ? [`${fmt(s.logged.sessions)} logged`, s.logged.minutes ? `${fmt(s.logged.minutes)} min` : '', s.logged.reps ? `${fmt(s.logged.reps)} reps` : '']
      .filter(Boolean).join(' \u00b7 ')
    : 'Nothing logged yet';
  return `
    <button type="button" class="skcard" data-open="${esc(s.id)}">
      <span class="skcard-h">
        <b>${esc(s.label)}</b>
        <span class="pill ${st.cls}">${esc(st.label)}</span>
      </span>
      <span class="skcard-s">${esc(s.summary)}</span>
      <span class="skcard-m">${esc(bits)}${s.measured ? ' \u00b7 tracked in the app' : ''}</span>
    </button>`;
}

function logRow(e, title, sub) {
  const bits = [e.minutes ? `${fmt(e.minutes)} min` : '', e.reps ? `${fmt(e.reps)} reps` : '', e.rating ? `felt ${e.rating} of 5` : '']
    .filter(Boolean).join(' \u00b7 ');
  return `
    <div class="skrow">
      <div class="skrow-t">
        <b>${esc(title)}</b>
        ${sub ? `<span>${esc(sub)}</span>` : ''}
        ${e.note ? `<em>${esc(e.note)}</em>` : ''}
      </div>
      <div class="skrow-m"><span>${esc(bits)}</span><small>${esc(stamp(e.at))}</small></div>
      <button class="btn skdel" data-del="${esc(e.id)}" title="Delete this entry">Delete</button>
    </div>`;
}

/* =============================================================== one skill */

/** Open one skill from outside, e.g. the Body Map's "Try this drill". */
export function open(id) { openSkill(id); }

function openSkill(id) {
  if (!id) return;
  S.open = id;
  S.detail = null; S.detailErr = null;
  S.form = blankForm();
  paint();
  loadSkill(id);
}

function closeSkill() {
  S.open = null; S.detail = null; S.detailErr = null;
  paint();
}

function paintDetail() {
  if (S.detailErr) {
    panel.innerHTML = `<div class="card"><h3>Skills</h3><p class="sub">${esc(S.detailErr)}</p>
      <div class="skacts"><button class="btn" id="skBack">Back to the tree</button>
      <button class="btn" id="skRetry2">Try again</button></div></div>`;
    bind('#skBack', 'click', closeSkill);
    bind('#skRetry2', 'click', () => loadSkill(S.open));
    return;
  }
  if (!S.detail) { panel.innerHTML = '<div class="card"><h3>Skills</h3><p class="sub">Opening that one.</p></div>'; return; }

  const d = S.detail;
  const s = d.skill;
  const lg = d.logged || {};

  panel.innerHTML = `
    <div class="skhead">
      <button class="btn" id="skBack">Back to the tree</button>
      <div><h2>${esc(s.label)}</h2><p class="sub">${esc(s.summary)}</p></div>
    </div>

    <div class="card">
      <h3>Where you are</h3>
      <div class="grid-stats">
        <div class="stat"><b>${fmt(lg.sessions)}</b><small>drills you logged</small></div>
        ${lg.minutes ? `<div class="stat"><b>${fmt(lg.minutes)}</b><small>minutes you logged</small></div>` : ''}
        ${lg.reps ? `<div class="stat"><b>${fmt(lg.reps)}</b><small>reps you logged</small></div>` : ''}
        ${lg.avgRating ? `<div class="stat"><b>${esc(lg.avgRating)}</b><small>how it felt, out of 5</small></div>` : ''}
        ${d.tracked ? `<div class="stat"><b>${fmt(d.tracked.attempts)}</b><small>attempts tracked in the app</small></div>` : ''}
      </div>
      <p class="sub" style="margin-top:12px">${lg.sessions
        ? `Last worked ${esc(whenLabel(lg.lastAt))}. Minutes and reps are what you typed in.`
        : 'Nothing logged against this one yet. Pick a drill below and put the first one in.'}</p>
      ${s.note ? `<p class="skplain">${esc(s.note)}</p>` : ''}
    </div>

    <div class="card">
      <h3>What good looks like</h3>
      <ul class="skgood">${(s.good || []).map((g) => `<li>${esc(g)}</li>`).join('')}</ul>
    </div>

    <div class="card">
      <h3>Drills</h3>
      <div class="skdrills">
        ${(s.drills || []).map((dr, i) => `
          <div class="skd">
            <div><b>${esc(dr.name)}</b><p>${esc(dr.how)}</p></div>
            <button class="btn" data-pick="${i}">Log this</button>
          </div>`).join('')}
      </div>
    </div>

    ${studiedCard(d)}

    <div class="card">
      <h3>Who your companion studied</h3>
      <div class="skpeople">
        ${(s.specialists || []).map((sp) => `
          <div class="skp"><b>${esc(sp.name)}</b><span>${esc(sp.known)}</span></div>`).join('')
        || '<p class="sub">No specialists listed for this one yet.</p>'}
      </div>
      <p class="skplain">${esc(d.note)}</p>
    </div>

    ${formCard(s)}

    ${d.log?.length ? `
      <div class="card">
        <h3>Your log for ${esc(s.label)}</h3>
        <div class="skrows">${d.log.map((e) => logRow(e, e.drill || s.label, '')).join('')}</div>
      </div>` : ''}`;

  bind('#skBack', 'click', closeSkill);
  bind('[data-pick]', 'click', (e) => {
    const dr = (s.drills || [])[Number(e.currentTarget.dataset.pick)];
    if (!dr) return;
    // Set the select in place rather than repainting, so the page does not
    // jump back to the top the moment they pick a drill.
    S.form.pick = dr.name; S.form.other = '';
    const sel = panel.querySelector('#skDrill');
    if (sel) sel.value = dr.name;
    const mins = panel.querySelector('#skMinutes');
    mins?.focus();
    mins?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
  bindForm(s);
  bind('[data-del]', 'click', (e) => removeEntry(e.currentTarget.dataset.del));
}

function studiedCard(d) {
  const list = d.studied || [];
  if (!list.length) {
    return `<div class="card">
      <h3>What it read</h3>
      <p class="sub">Nothing filed for this skill yet. Your companion studies public coaching and film for each skill in turn, and what it reads shows up here with the source next to it.</p>
    </div>`;
  }
  return `<div class="card">
    <h3>What it read</h3>
    <p class="sub">Public coaching and film your companion studied for this skill, with where each one came from.</p>
    <div class="sknodes">
      ${list.map((n) => {
        const href = safeUrl(n.source?.url);
        return `
        <div class="sknode">
          ${n.athlete ? `<b>${esc(n.athlete)}</b>` : ''}
          <p>${esc(n.insight)}</p>
          ${href
            ? `<a class="sksrc" href="${esc(href)}" target="_blank" rel="noopener noreferrer">${esc(n.source.title || href)}</a>`
            : '<span class="sksrc none">Source not recorded</span>'}
        </div>`;
      }).join('')}
    </div>
  </div>`;
}

/* ---------------------------------------------------------------- the form */

function formCard(s) {
  const names = (s.drills || []).map((d) => d.name);
  const picked = S.form.pick === OTHER || names.includes(S.form.pick) ? S.form.pick : '';
  return `
    <div class="card">
      <h3>Log what you did</h3>
      <div class="skform">
        <label class="sklab wide">Drill
          <select id="skDrill">
            <option value="">Pick a drill</option>
            ${names.map((n) => `<option value="${esc(n)}" ${picked === n ? 'selected' : ''}>${esc(n)}</option>`).join('')}
            <option value="${OTHER}" ${picked === OTHER ? 'selected' : ''}>Something else</option>
          </select>
        </label>
        ${picked === OTHER ? `<label class="sklab wide">What you did
          <input type="text" id="skOther" maxlength="80" placeholder="Name the drill" value="${esc(S.form.other)}"></label>` : ''}
        <label class="sklab">Minutes
          <input type="number" id="skMinutes" inputmode="numeric" min="0" max="600" step="1" value="${esc(S.form.minutes)}"></label>
        <label class="sklab">Reps
          <input type="number" id="skReps" inputmode="numeric" min="0" max="5000" step="1" value="${esc(S.form.reps)}"></label>
        <label class="sklab wide">Note
          <input type="text" id="skNote" maxlength="300" placeholder="Anything worth remembering" value="${esc(S.form.note)}"></label>
      </div>
      <div class="skscale">
        <div class="skscale-h"><b>How did it feel</b><small>Optional. One tap.</small></div>
        <div class="skpick">${RATINGS.map((v) =>
          `<button type="button" data-rate="${v}" class="${S.form.rating === v ? 'on' : ''}" aria-pressed="${S.form.rating === v}">${v}</button>`).join('')}</div>
        <div class="skends"><span>Rough</span><span>Sharp</span></div>
      </div>
      <div class="skacts">
        <button class="btn primary" id="skSave">Save it</button>
        <button class="btn" id="skClear">Start over</button>
      </div>
      <p class="sub" style="margin-top:12px">Minutes and reps are yours: nothing here is estimated for you. Put in one of the two.</p>
    </div>`;
}

function bindForm(s) {
  const names = (s.drills || []).map((d) => d.name);
  bind('#skDrill', 'change', (e) => {
    const v = e.target.value;
    const wasOther = S.form.pick === OTHER;
    S.form.pick = v === OTHER || names.includes(v) ? v : '';
    if (S.form.pick !== OTHER) S.form.other = '';
    // The free text box only exists in one of the two states, so a repaint is
    // needed exactly when the state flips.
    if (wasOther !== (S.form.pick === OTHER)) { paint(); panel.querySelector('#skOther')?.focus(); }
  });
  bind('#skOther', 'input', (e) => { S.form.other = e.target.value; });
  bind('#skMinutes', 'input', (e) => { S.form.minutes = e.target.value; });
  bind('#skReps', 'input', (e) => { S.form.reps = e.target.value; });
  bind('#skNote', 'input', (e) => { S.form.note = e.target.value; });
  bind('[data-rate]', 'click', (e) => {
    const v = Number(e.currentTarget.dataset.rate);
    S.form.rating = S.form.rating === v ? null : v;
    panel.querySelectorAll('[data-rate]').forEach((b) => {
      const on = Number(b.dataset.rate) === S.form.rating;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', String(on));
    });
  });
  bind('#skSave', 'click', save);
  bind('#skClear', 'click', () => { S.form = blankForm(); paint(); });
}

async function save() {
  const s = S.detail?.skill;
  if (!s) return;
  const chosen = S.form.pick === OTHER ? String(S.form.other || '').trim() : String(S.form.pick || '').trim();
  const minutes = String(S.form.minutes || '').trim();
  const reps = String(S.form.reps || '').trim();
  if (!minutes && !reps) return ctx.toast('Put in the minutes or the reps you actually did.', true);
  if (!chosen) return ctx.toast('Pick a drill, or name what you did.', true);

  const body = { skill: s.id, drill: chosen };
  if (minutes) body.minutes = Number(minutes);
  if (reps) body.reps = Number(reps);
  if (S.form.rating) body.rating = S.form.rating;
  if (S.form.note.trim()) body.note = S.form.note.trim();

  const btns = panel.querySelectorAll('button');
  btns.forEach((b) => { b.disabled = true; });
  try {
    await ctx.api('/api/skills/drill', { method: 'POST', body: JSON.stringify(body) });
    S.form = blankForm();
    ctx.toast('Logged. That is on your record.');
    await refresh();
  } catch (e) {
    ctx.toast(e.message, true);
    btns.forEach((b) => { b.disabled = false; });
  }
}

async function removeEntry(id) {
  if (!id) return;
  const ok = await ctx.ui.confirm({
    title: 'Delete this entry?',
    body: 'It comes off your skill log and your companion stops counting it.',
    confirmText: 'Delete', danger: true,
  });
  if (!ok) return;
  try {
    await ctx.api(`/api/skills/drill/${encodeURIComponent(id)}`, { method: 'DELETE' });
    ctx.toast('Gone.');
    await refresh();
  } catch (e) { ctx.toast(e.message, true); }
}
